import { MenuProps } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RouteConstants, SideMenuItemKeys } from "../../../../constants/ApplicationConstants/RouteConstants";
import { toggleCollapsedSideNav } from "../../../../appRedux/reducers/Common";

import type { RootState } from "../../../../appRedux/store";
import { Modules } from "../../../../constants/DatabaseConstants/MasterConstant";

export const useSideBarContent = () => {
  /* CONSTANTS */
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();
  const { themeType } = useSelector(({ settings }: RootState) => settings);

  /* eslint-disable-next-line */
  const { navCollapsed, reRenderSideBar } = useSelector(({ common }: RootState) => common);
  const { pathname } = location;

  /* STATE */
  const [selectedKeys, setSelectedKeys] = useState<string>("");

  /* HANDLERS */
  const handleNavCollapse = () => dispatch(toggleCollapsedSideNav(!navCollapsed));

  const handleOnClick: MenuProps["onClick"] = (menuItem) => {
    handleNavCollapse();

    const keyPath = menuItem.keyPath.at(0);

    switch (keyPath) {
      case SideMenuItemKeys.DEFAULT_HOME_PAGE: {
        return navigate(RouteConstants.DEFAULT_HOME_PAGE);
      }

      case SideMenuItemKeys.USER_MANAGEMENT: {
        return navigate(`${RouteConstants.USER_MANAGEMENT_PAGE}/${RouteConstants.USER_LIST_PAGE}`);
      }

      case SideMenuItemKeys.MASTER_MANAGEMENT: {
        return navigate(`${RouteConstants.MASTER_MANAGEMENT_PAGE}/${Modules.CUSTOMER_MASTER}`);
      }

      case SideMenuItemKeys.DISPLAY_QUEUE_STATUS: {
        return navigate(RouteConstants.DISPLAY_QUEUE_STATUS_PAGE);
      }

      case SideMenuItemKeys.DISPLAY_SCHEDULE_LIST: {
        return navigate(RouteConstants.DISPLAY_SCHEDULE_LIST_PAGE);
      }
      case SideMenuItemKeys.MANUAL_BOOKING_RETRIEVAL: {
        return navigate(RouteConstants.BOOKING_RETRIEVAL_REQUEST_PAGE);
      }

      case SideMenuItemKeys.EMERGENCY_RETRIEVAL_REQUEST: {
        return navigate(RouteConstants.EMERGENCY_RETRIEVAL_REQUEST_PAGE);
      }

      case SideMenuItemKeys.DEAD_PARKING_UPDATE: {
        return navigate(RouteConstants.DEAD_PARKING_UPDATE_PAGE);
      }

      case SideMenuItemKeys.PARKING_OCCUPANCY_MONITORING: {
        return navigate(RouteConstants.PARKING_OCCUPANCY_MONITORING_PAGE);
      }

      case SideMenuItemKeys.PARKING_PRICE_MANAGEMENT: {
        return navigate(RouteConstants.PARKING_PRICE_MANAGEMENT_PAGE);
      }

      case SideMenuItemKeys.USER_FEEDBACK: {
        return navigate(RouteConstants.USER_FEEDBACK);
      }

      case SideMenuItemKeys.PARKING_HISTORY: {
        return navigate(RouteConstants.PARKING_HISTORY);
      }
      case SideMenuItemKeys.SUPPORT_QUERY: {
        return navigate(RouteConstants.SUPPORT_QUERY);
      }

      case SideMenuItemKeys.MESSAGING: {
        return navigate(RouteConstants.MESSAGING);
      }

      case SideMenuItemKeys.CONCURRENT_REQUEST: {
        return navigate(RouteConstants.CONCURRENT_REQUEST);
      }

      case SideMenuItemKeys.REQUEST_PER_HOUR_REPORT: {
        return navigate(RouteConstants.REQUEST_PER_HOUR_REPORT);
      }

      case SideMenuItemKeys.VISITORS_REPORT: {
        return navigate(RouteConstants.VISITORS_REPORT);
      }

      case SideMenuItemKeys.ALL_USERS_PARKING_REPORT: {
        return navigate(RouteConstants.ALL_USERS_PARKING_REPORT);
      }

      case SideMenuItemKeys.PARKING_DATA_COUNTS_REPORT: {
        return navigate(RouteConstants.PARKING_DATA_COUNTS_REPORT);
      }

      case SideMenuItemKeys.ADVANCE_BOOKING_REQUEST: {
        return navigate(RouteConstants.ADVANCE_BOOKING_REQUEST);
      }

      case SideMenuItemKeys.ADVANCE_BOOKING_REQUEST_REPORT: {
        return navigate(RouteConstants.ADVANCE_BOOKING_REQUEST_REPORT);
      }

      case SideMenuItemKeys.IOTHUB_LOGS: {
        return navigate(RouteConstants.IOTHUB_LOGS);
      }

      case SideMenuItemKeys.CLIENT_PARKING_AVAILABILITY_LIST: {
        return navigate(RouteConstants.CLIENT_PARKING_AVAILABILITY_LIST);
      }

      case SideMenuItemKeys.ABR_PENALTY_COLLECTION_REPORT: {
        return navigate(RouteConstants.ABR_PENALTY_COLLECTION_REPORT);
      }

      case SideMenuItemKeys.LOCATION_STATUS_LIST: {
        return navigate(RouteConstants.LOCATION_STATUS_LIST);
      }

      /* If no keypath found then move to coming-soon page */
      default:
        return navigate(RouteConstants.PAGE_NOT_FOUND);
    }
  };

  useEffect(() => {
    const pathSegments = pathname.split("/");

    if (!pathSegments?.length) return;

    if (pathSegments.length <= 2) {
      /* last index element is the menu item, in case of home page */
      /* required by home page */
      setSelectedKeys(pathSegments.at(-1)!);
    } else {
      /* 0th index will be empty string 
       1st index element will be portal 
       2nd index element will be menu 
      */

      setSelectedKeys(pathSegments[1]);
    }

    // eslint-disable-next-line
  }, [location.pathname, location.search]);

  return { handleOnClick, selectedKeys, themeType };
};
